import { createSlice } from '@reduxjs/toolkit';

import { fetchUsersPermissions, updateRule, updateUserPermissions, fetchRules, fetchTaxes, updateTax } from 'services';

import { RootState } from './store';
import { SettingsState } from './types';

const initialState: SettingsState = {
  usersPermissions: [],
  rules: [],
  taxes: [],
  isLoading: false,
  isSuccess: false,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    resetSettingsFormState(state) {
      state.isLoading = false;
      state.isSuccess = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchUsersPermissions.fulfilled, (state, { payload }) => {
      state.usersPermissions = payload;
    });
    builder.addCase(updateUserPermissions.fulfilled, (state, { payload }) => {
      state.usersPermissions = state.usersPermissions.filter(({ login }) => login !== payload.login).concat(payload);
    });
    builder.addCase(fetchRules.fulfilled, (state, { payload }) => {
      state.rules = payload;
      state.isLoading = false;
      state.isSuccess = false;
    });
    builder.addCase(fetchRules.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(fetchRules.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateRule.fulfilled, (state, { payload }) => {
      const updatedItemIndex = state.rules.findIndex(
        ({ compensationType, legalEntity }) =>
          compensationType === payload.compensationType && legalEntity === payload.legalEntity,
      );

      state.rules.splice(updatedItemIndex, 1, payload);
      state.isLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(updateRule.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateRule.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchTaxes.fulfilled, (state, { payload }) => {
      state.taxes = payload;
      state.isLoading = false;
      state.isSuccess = false;
    });
    builder.addCase(fetchTaxes.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(fetchTaxes.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateTax.fulfilled, (state, { payload }) => {
      const updatedItemIndex = state.taxes.findIndex(
        ({ compensationType, legalEntity }) =>
          compensationType === payload.compensationType && legalEntity === payload.legalEntity,
      );

      state.taxes.splice(updatedItemIndex, 1, payload);
      state.isLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(updateTax.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateTax.pending, (state) => {
      state.isLoading = true;
    });
  },
});

export const {
  actions: { resetSettingsFormState },
  reducer: settingsReducer,
} = settingsSlice;

export const getUsersPermissions = ({ settings }: RootState) => settings.usersPermissions;
export const getRules = ({ settings }: RootState) => settings.rules;
export const getTaxes = ({ settings }: RootState) => settings.taxes;
export const getLoading = ({ settings }: RootState) => settings.isLoading;
export const getUpdateSuccess = ({ settings }: RootState) => settings.isSuccess;
